import { create } from 'zustand';
import i18n from '../i18n/config';
// Utiliser les types API directement
import {
  CadrageReadWithTeam,
  CadrageUpdate, // Ajouter l'import pour CadrageUpdate
  Interview, // Utiliser le type Interview défini pour l'API
  Folder,
  Document,
  UseCaseDetail // Garder UseCaseDetail pour l'instant si updateUseCaseDetail l'utilise encore
} from '../types';
import cadrageService from '../services/cadrageService'; // Importer le service API
import useCaseService from '../services/useCaseService'; // Importer le service pour les use cases

// Remove mock data files; use neutral in-memory placeholders without external JSON
let missionsDatabase: any[] = [];
const documentTreeDatabase: { tree: Folder | null, documents: Document[] } = { tree: null, documents: [] };


interface CadrageState {
  cadrageData: CadrageReadWithTeam | null; // Utiliser le type API
  documentTree: Folder | null;
  currentUseCaseDetail: UseCaseDetail | null; // Utiliser UseCaseDetail pour correspondre aux actions
  isLoading: boolean;
  error: string | null;
  /** Raw error of the last loadCadrageData failure (lets the page tell a 404 of another org). */
  loadError: unknown;
  architectureNotesMd: string;
  architectureDiagramMd: string; // Syntaxe Mermaid pour le diagramme d'architecture
  databaseSchemaNotesMd: string; // Notes Markdown pour le schéma de DB
  // dbmlSchema: string; // Code DBML pour le schéma - REMPLACÉ PAR MERMAID
  mermaidSchema: string; // Code Mermaid pour le schéma
  // Utiliser le type Interview de l'API
  interviews: Interview[];
  selectedInterviewId: string | null;
}

interface CadrageActions {
  loadCadrageData: (cadrageId: string) => Promise<void>; // Déjà async
  addDocument: (newDocument: Document, parentFolderId: string) => void;
  addFolder: (newFolderData: Omit<Folder, 'childFolders' | 'childDocuments'>, parentFolderId: string) => void;
  updateUseCaseDetail: (newData: Partial<UseCaseDetail>) => Promise<void>; // Make it async for API call
  updateArchitectureNotesMd: (newMd: string) => void; // Met à jour l'état local uniquement
  saveArchitectureNotes: () => Promise<void>; // Sauvegarde architecture_notes via API
  updateArchitectureDiagramMd: (newMd: string) => void;
  updateDatabaseSchemaNotesMd: (newMd: string) => void; // Met à jour l'état local uniquement
  // updateDbmlSchema: (newDbml: string) => void; // REMPLACÉ PAR MERMAID
  // saveDbmlSchema: () => Promise<void>; // REMPLACÉ PAR MERMAID
  updateMermaidSchema: (newSchema: string) => void; // Met à jour l'état local uniquement
  saveSchemaAndNotes: () => Promise<void>; // Nouvelle action pour sauvegarder Schéma ET Notes via API
  // _findUseCaseDetailById: (projectId: string, useCaseId: string) => UseCaseDetail | undefined; // Supprimé car basé sur les mocks
  // Utiliser le type Interview de l'API
  selectInterview: (interviewId: string | null) => void;
  addInterview: (newInterview: Interview) => void; // Utiliser Interview
  updateInterview: (updatedInterview: Interview) => void; // Utiliser Interview
  // Garder UseCaseDetail pour l'instant
  addLinkedUseCase: (newUseCase: UseCaseDetail) => void;
  // Utiliser any pour l'instant pour addCadrage car Project n'est plus utilisé
  addCadrage: (newCadrage: any) => void;
}

// Supprimer le helper findUseCaseDetail basé sur les mocks
// const findUseCaseDetail = ...

export const useCadrageStore = create<CadrageState & CadrageActions>((set, get) => ({
  cadrageData: null,
  currentUseCaseDetail: null, // Initialisé à null
  isLoading: false,
  error: null,
  loadError: null,
  architectureNotesMd: '',
  architectureDiagramMd: '',
  databaseSchemaNotesMd: '',
  // dbmlSchema: '', // REMPLACÉ PAR MERMAID
  mermaidSchema: '', // Initialiser le nouvel état pour Mermaid
  // Initialisation état entretiens
  interviews: [],
  selectedInterviewId: null,
  // Initialisation état arbre documentaire
  documentTree: null,

  // Supprimer _findUseCaseDetailById basé sur les mocks
  // _findUseCaseDetailById: ... ,

  loadCadrageData: async (cadrageId: string) => {
    // Réinitialiser l'état avant le chargement
    set({
      isLoading: true,
      error: null,
      loadError: null,
      cadrageData: null,
      currentUseCaseDetail: null,
      architectureNotesMd: '', // Garder la réinitialisation des champs spécifiques
      architectureDiagramMd: '',
      databaseSchemaNotesMd: '',
      // dbmlSchema: '', // REMPLACÉ PAR MERMAID
      mermaidSchema: '', // Réinitialiser aussi pour Mermaid
      interviews: [],
      selectedInterviewId: null,
      documentTree: null
    });

    try {
      // Appeler le service pour récupérer les données du cadrage
      const cadrage = await cadrageService.getCadrageById(cadrageId);
      // console.log('[STORE loadCadrageData] Cadrage brut reçu:', cadrage); // DEBUG - Supprimé
      // console.log('[STORE loadCadrageData] Valeur de cadrage.mermaid_schema:', cadrage.mermaid_schema); // DEBUG - Supprimé

      // Appeler le service pour récupérer le cas d'usage lié
      const linkedUseCase = await cadrageService.getLinkedUseCase(cadrageId);

      // Appeler l'API pour récupérer les entretiens liés au cadrage
      let interviews: Interview[] = [];
      try {
          // Utiliser cadrageService pour charger les entretiens
          interviews = await cadrageService.getInterviews(cadrageId);
          console.log(`Entretiens chargés pour cadrage ${cadrageId}:`, interviews);
      } catch (interviewError: any) { // Ajouter ': any' pour typer l'erreur attrapée
          const errorMsg = interviewError.response?.data?.detail || interviewError.message || i18n.t('errors.cadrage.interviewsLoadUnknown');
          console.error(`Erreur lors du chargement des entretiens pour le cadrage ${cadrageId}:`, errorMsg);
          // Mettre à jour l'état d'erreur global pour que l'UI puisse réagir
          set({ error: i18n.t('errors.cadrage.interviewsLoad', { message: errorMsg }) });
          // Laisser interviews comme un tableau vide, l'erreur sera affichée
      }


      // Charger l'arbre documentaire (logique simulée conservée pour l'instant)
      let loadedTree: Folder | null = null;
      // Remplacer cadrage.documentTreeRootId par une logique API si nécessaire
      // if (cadrage.documentTreeRootId) { // Supposons que documentTreeRootId n'est pas dans CadrageReadWithTeam
      if (cadrageId === 'cad-doc-test') { // Condition de simulation
          loadedTree = JSON.parse(JSON.stringify(documentTreeDatabase.tree));
      }

      // Mettre à jour l'état avec les données récupérées
      set({
        cadrageData: cadrage,
        currentUseCaseDetail: linkedUseCase, // Stocker le UseCase récupéré
        documentTree: loadedTree,
        // Pré-remplir les champs spécifiques depuis les données du cadrage API
        architectureNotesMd: cadrage.architecture_notes || '',
        // If an 'architecture_diagram' field exists later, wire it here
        architectureDiagramMd: '',
        databaseSchemaNotesMd: cadrage.db_schema_notes || '',
        // dbmlSchema: cadrage.dbml_schema || '', // REMPLACÉ PAR MERMAID
        mermaidSchema: cadrage.mermaid_schema || '', // Charger le champ mermaid_schema (s'assurer qu'il existe sur le type CadrageReadWithTeam)
        // Charger les entretiens récupérés depuis l'API
        interviews: interviews,
        selectedInterviewId: null,
        isLoading: false,
      });

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      set({ error: i18n.t('errors.apiError', { message: errorMsg }), loadError: err, isLoading: false });
      console.error("Erreur lors du chargement des données du cadrage via API:", err);
    }
  },

  // Updated updateUseCaseDetail to make actual API call
  updateUseCaseDetail: async (newData: Partial<UseCaseDetail>) => {
    const state = get();
    if (!state.currentUseCaseDetail || !state.cadrageData) {
      console.error("Impossible de mettre à jour : currentUseCaseDetail ou cadrageData non chargé.");
      throw new Error(i18n.t('errors.cadrage.useCaseOrCadrageNotLoaded'));
    }

    const useCaseId = state.currentUseCaseDetail.id;

    try {
      // Make API call to update use case
      const updatedUseCase = await useCaseService.update(useCaseId, newData);

      // Update local state with the response from API
      set({
        currentUseCaseDetail: updatedUseCase,
      });

      console.log("UseCaseDetail mis à jour avec succès via API:", updatedUseCase);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      console.error("Erreur lors de la mise à jour du cas d'usage:", err);
      throw new Error(i18n.t('errors.apiError', { message: errorMsg }));
    }
  },

  updateArchitectureNotesMd: (newMd: string) => {
    // Met simplement à jour l'état local. La sauvegarde se fera via saveArchitectureNotes.
    set({ architectureNotesMd: newMd });
  },

  saveArchitectureNotes: async () => {
    const { cadrageData, architectureNotesMd } = get();
    if (!cadrageData) {
      throw new Error(i18n.t('errors.cadrage.notLoaded'));
    }
    const updated = await cadrageService.updateCadrage(cadrageData.id, {
      architecture_notes: architectureNotesMd,
    });
    // Keep cadrageData in sync so other readers of the store don't see stale notes.
    set({ cadrageData: { ...cadrageData, architecture_notes: updated.architecture_notes } });
  },

  updateArchitectureDiagramMd: (newMd: string) => {
    set(state => {
      if (!state.cadrageData) return {};
      const cadrageIndex = missionsDatabase.findIndex(p => p.id === state.cadrageData!.id);
      if (cadrageIndex !== -1) {
        const updatedCadrage = { ...missionsDatabase[cadrageIndex], architectureDiagramMd: newMd };
        missionsDatabase[cadrageIndex] = updatedCadrage;
        return { architectureDiagramMd: newMd, cadrageData: updatedCadrage };
      }
      console.warn("Cadrage non trouvé pour sauvegarde architectureDiagramMd.");
      return { architectureDiagramMd: newMd };
    });
  },

  updateDatabaseSchemaNotesMd: (newMd: string) => {
    // Met simplement à jour l'état local. La sauvegarde se fera via saveSchemaAndNotes.
    set({ databaseSchemaNotesMd: newMd });
  },

  // updateDbmlSchema: (newDbml: string) => {
  //   // Met simplement à jour l'état local. La sauvegarde se fera via saveDbmlSchema.
  //   set({ dbmlSchema: newDbml });
  // },

  // saveDbmlSchema: async () => {
  //   const { cadrageData, dbmlSchema } = get();
  //   if (!cadrageData) {
  //     console.error("Impossible de sauvegarder le schéma DBML : Cadrage non chargé.");
  //     throw new Error("Cadrage non chargé."); // Lever une erreur pour le composant UI
  //   }
  //   set({ isLoading: true, error: null }); // Optionnel: indiquer un chargement pendant la sauvegarde
  //   try {
  //     await cadrageService.updateCadrage(cadrageData.id, { dbml_schema: dbmlSchema });
  //     set({ isLoading: false });
  //     // Optionnel: recharger les données ou juste confirmer le succès
  //     console.log("Schéma DBML sauvegardé avec succès.");
  //   } catch (err) {
  //     const errorMsg = err instanceof Error ? err.message : String(err);
  //     console.error("Erreur lors de la sauvegarde du schéma DBML:", err);
  //     set({ error: `Erreur API: ${errorMsg}`, isLoading: false });
  //     throw err; // Propager l'erreur pour que le composant UI puisse réagir
  //   }
  // },

  // Actions pour Mermaid
  updateMermaidSchema: (newSchema: string) => {
    // Met simplement à jour l'état local. La sauvegarde se fera via saveMermaidSchema.
    set({ mermaidSchema: newSchema });
  },

  saveSchemaAndNotes: async () => {
    const { cadrageData, mermaidSchema, databaseSchemaNotesMd } = get();
    if (!cadrageData) {
      console.error("Impossible de sauvegarder : Cadrage non chargé.");
      throw new Error(i18n.t('errors.cadrage.notLoaded'));
    }
    // console.log('[STORE saveSchemaAndNotes] Tentative de sauvegarde avec cadrage ID:', cadrageData.id); // DEBUG - Supprimé
    // console.log('[STORE saveSchemaAndNotes] Valeur de mermaidSchema:', mermaidSchema); // DEBUG - Supprimé
    // console.log('[STORE saveSchemaAndNotes] Valeur de databaseSchemaNotesMd:', databaseSchemaNotesMd); // DEBUG - Supprimé
    // set({ isLoading: true, error: null }); // Utiliser un état de chargement spécifique si besoin

    // Préparer le payload avec les deux champs
    const payload: Partial<CadrageUpdate> = { // Utiliser le type importé CadrageUpdate
      mermaid_schema: mermaidSchema,
      db_schema_notes: databaseSchemaNotesMd, // Assurez-vous que ce nom correspond au champ dans CadrageUpdate
    };

    try {
      // Assurez-vous que le service et le type de payload pour updateCadrage
      // supportent la mise à jour de 'mermaid_schema' et 'db_schema_notes'.
      await cadrageService.updateCadrage(cadrageData.id, payload);
      // set({ isLoading: false });
      console.log("Schéma Mermaid et Notes sauvegardés avec succès.");
    } catch (err) {
      console.error("Erreur lors de la sauvegarde du schéma Mermaid et/ou des notes:", err);
      // set({ error: `Erreur API: ${errorMsg}`, isLoading: false });
      throw err; // Propager l'erreur pour que le composant UI puisse réagir
    }
  },

  // --- Actions pour les entretiens ---

  selectInterview: (interviewId: string | null) => {
    set({ selectedInterviewId: interviewId });
  },

  // Adapter addInterview pour utiliser le type Interview
  addInterview: (newInterview: Interview) => {
    set(state => {
      if (!state.cadrageData) {
        console.warn("Impossible d'ajouter l'entretien : cadrageData non chargé.");
        return {};
      }

      const updatedInterviews = [...state.interviews, newInterview];

      // --- Simulation de la sauvegarde dans la "base de données" en mémoire ---
      const cadrageIndex = missionsDatabase.findIndex(p => p.id === state.cadrageData!.id);
      if (cadrageIndex !== -1) {
        const updatedCadrage = {
          ...missionsDatabase[cadrageIndex],
          guidedInterviews: updatedInterviews
        };
        missionsDatabase[cadrageIndex] = updatedCadrage;

        // Met à jour le state Zustand
        return {
          interviews: updatedInterviews,
          cadrageData: updatedCadrage // Mettre à jour cadrageData pour refléter la sauvegarde
        };
      } else {
         console.warn("Cadrage non trouvé pour sauvegarde addInterview.");
         // On met quand même à jour l'état local, mais la persistance simulée échoue
         return { interviews: updatedInterviews };
      }
    });
  },

  // Adapter updateInterview pour utiliser le type Interview
  updateInterview: (updatedInterview: Interview) => {
    set(state => {
      if (!state.cadrageData) {
        console.warn("Impossible de mettre à jour l'entretien : cadrageData non chargé.");
        return {};
      }

      const updatedInterviews = state.interviews.map(interview =>
        interview.id === updatedInterview.id ? updatedInterview : interview
      );

      // --- Simulation de la sauvegarde dans la "base de données" en mémoire ---
      const cadrageIndex = missionsDatabase.findIndex(p => p.id === state.cadrageData!.id);
      if (cadrageIndex !== -1) {
        const updatedCadrage = {
          ...missionsDatabase[cadrageIndex],
          guidedInterviews: updatedInterviews
        };
        missionsDatabase[cadrageIndex] = updatedCadrage;

        // Met à jour le state Zustand
        return {
          interviews: updatedInterviews,
          cadrageData: updatedCadrage // Mettre à jour cadrageData pour refléter la sauvegarde
        };
      } else {
        console.warn("Cadrage non trouvé pour sauvegarde updateInterview.");
        // On met quand même à jour l'état local, mais la persistance simulée échoue
        return { interviews: updatedInterviews };
      }
    });
  },

  // --- Actions pour l'arbre documentaire ---

  addDocument: (newDocument: Document, parentFolderId: string) => {
    set(state => {
      if (!state.documentTree) return {};

      // Fonction helper récursive immuable pour ajouter un document
      const addDocRecursive = (folder: Folder): Folder => {
        let newChildDocuments = folder.childDocuments;
        if (folder.id === parentFolderId) {
          // Crée une nouvelle liste de documents enfants avec le nouveau document
          newChildDocuments = [...folder.childDocuments, newDocument];
        }

        // Traite récursivement les sous-dossiers
        const newChildFolders = folder.childFolders.map(child => addDocRecursive(child));

        // Si les enfants n'ont pas changé, retourne le dossier original pour optimiser
        if (newChildDocuments === folder.childDocuments && newChildFolders === folder.childFolders) {
          return folder;
        }

        // Retourne un nouveau dossier avec les enfants mis à jour
        return {
          ...folder,
          childDocuments: newChildDocuments,
          childFolders: newChildFolders,
        };
      };

      const newTree = addDocRecursive(state.documentTree);

      // Met à jour l'état seulement si l'arbre a réellement changé
      return newTree !== state.documentTree ? { documentTree: newTree } : {};
    });
  },

  addFolder: (newFolderData: Omit<Folder, 'childFolders' | 'childDocuments'>, parentFolderId: string) => {
    set(state => {
      if (!state.documentTree) return {};

       // Crée l'objet Folder complet
       const newFolder: Folder = {
        ...newFolderData,
        childFolders: [],
        childDocuments: [],
      };

      // Fonction helper récursive immuable pour ajouter un dossier
      const addFolderRecursive = (folder: Folder): Folder => {
        let newChildFolders = folder.childFolders;
        if (folder.id === parentFolderId) {
          // Crée une nouvelle liste de dossiers enfants avec le nouveau dossier
          newChildFolders = [...folder.childFolders, newFolder];
        } else {
           // Traite récursivement les sous-dossiers seulement si ce n'est pas le parent direct
           newChildFolders = folder.childFolders.map(child => addFolderRecursive(child));
        }

        // Si les dossiers enfants n'ont pas changé, retourne le dossier original
        if (newChildFolders === folder.childFolders) {
          return folder;
        }

        // Retourne un nouveau dossier avec les enfants mis à jour
        return {
          ...folder,
          childFolders: newChildFolders,
          // Les documents enfants ne sont pas affectés par l'ajout d'un dossier
          childDocuments: folder.childDocuments
        };
      };

      const newTree = addFolderRecursive(state.documentTree);

      // Met à jour l'état seulement si l'arbre a réellement changé
      return newTree !== state.documentTree ? { documentTree: newTree } : {};
    });
  },

  // --- Action pour ajouter un cas d'usage lié ---
  addLinkedUseCase: (newUseCase: UseCaseDetail) => {
    set(state => {
      if (!state.cadrageData) {
        console.warn("Impossible d'ajouter le cas d'usage : cadrageData non chargé.");
        return {};
      }

      // Assurer que les nouveaux champs ont des valeurs par défaut
      const fullNewUseCase = {
        ...newUseCase,
        // Utiliser les noms snake_case du type UseCaseDetail
        business_need: newUseCase.business_need || '',
        input: newUseCase.input || '', // input/output semblent corrects
        output: newUseCase.output || '',
        involved_tools: newUseCase.involved_tools || [],
      };

      // --- Simulation de la sauvegarde dans la "base de données" en mémoire ---
      const cadrageIndex = missionsDatabase.findIndex(p => p.id === state.cadrageData!.id);
      if (cadrageIndex !== -1) {
        const updatedCadrage = { ...missionsDatabase[cadrageIndex] };

        // S'assurer que useCasesDetail existe
        if (!updatedCadrage.useCasesDetail) {
          updatedCadrage.useCasesDetail = [];
        }

        // Vérifier si un cas d'usage avec cet ID existe déjà
        // Ajouter le type explicite pour 'uc'
        const existingIndex = updatedCadrage.useCasesDetail.findIndex((uc: UseCaseDetail) => uc.id === fullNewUseCase.id);
        if (existingIndex === -1) {
          updatedCadrage.useCasesDetail.push(fullNewUseCase);
        } else {
          // Remplacer si existe déjà (ou logguer une erreur, selon la logique souhaitée)
          console.warn(`Un cas d'usage avec l'ID ${fullNewUseCase.id} existe déjà dans ce cadrage. Il va être remplacé.`);
          updatedCadrage.useCasesDetail[existingIndex] = fullNewUseCase;
        }

        // Mettre à jour la "base de données"
        missionsDatabase[cadrageIndex] = updatedCadrage;

        // Met à jour le state Zustand
        return {
          currentUseCaseDetail: fullNewUseCase, // Mettre à jour le cas d'usage courant
          cadrageData: updatedCadrage // Mettre à jour cadrageData pour refléter la sauvegarde
        };
      } else {
        console.warn("Cadrage non trouvé pour sauvegarde addLinkedUseCase.");
        // On met quand même à jour l'état local, mais la persistance simulée échoue
        return { currentUseCaseDetail: fullNewUseCase };
      }
    });
  },


  // --- Action pour ajouter un nouveau cadrage (utiliser any pour l'instant) ---
  addCadrage: (newCadrage: any) => {
    // --- Simulation de la sauvegarde dans la "base de données" en mémoire ---
    // Cette logique devra être remplacée par un appel API via cadrageService.createCadrage
    // Vérifier si l'ID existe déjà
    const existingIndex = missionsDatabase.findIndex(p => p.id === newCadrage.id);
    if (existingIndex === -1) {
      missionsDatabase.push(newCadrage);
      console.log("Nouveau cadrage ajouté à la base de données simulée:", newCadrage);

      // Optionnel: Mettre à jour le UseCaseDetail lié (s'il existe) pour ajouter le cadrageId
      // Note: Ceci modifie potentiellement un objet géré par useAuditStore,
      // ce qui n'est pas idéal mais nécessaire avec la structure actuelle des mocks.
      if (newCadrage.sourceUseCaseId) {
        let found = false;
        missionsDatabase = missionsDatabase.map((project: any) => { // Ajouter type any pour project
          if (project.useCasesDetail) {
            // Ajouter le type explicite pour 'uc'
            project.useCasesDetail = project.useCasesDetail.map((uc: UseCaseDetail) => {
              if (uc.id === newCadrage.sourceUseCaseId) {
                found = true;
                return { ...uc, cadrageId: newCadrage.id }; // Ajouter le lien vers le cadrage
              }
              return uc;
            });
          }
          return project;
        });
        if (found) {
          console.log(`Cas d'usage ${newCadrage.sourceUseCaseId} mis à jour avec cadrageId ${newCadrage.id}`);
        } else {
           console.warn(`Cas d'usage source ${newCadrage.sourceUseCaseId} non trouvé pour ajout du cadrageId.`);
        }
      }

    } else {
      console.error(`Un cadrage avec l'ID ${newCadrage.id} existe déjà.`);
      // Gérer l'erreur ? Pour l'instant, on ne fait rien d'autre.
    }

    // Note: Cette action ne modifie pas directement l'état de ce store (useCadrageStore),
    // car ce store gère les données d'UN cadrage chargé, pas la liste globale.
    // La redirection après création mènera à la liste qui (on suppose) relira les données globales.
  },

}));

// Optionnel: Exposer la base de données pour debug ou tests
export const getMissionsDatabase = () => missionsDatabase;
export const resetMissionsDatabase = () => {
  missionsDatabase = [];
};
